import React, { useState, useEffect } from "react"
import moment from "moment"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import KPICard from "./KPICard"
import TableKPICard from "./TableKPICard"
import SpamTicketsDialog from "./SpamTicketsDialog"
import {
  ClockIcon,
  TicketIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  TagIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline"
import { GetAgents, GetTickets } from "./api"
import useCSStatistics from "./useCSStatistics"

/**
 * Customer Support Overview component
 * Displays KPIs and statistics for the support system
 */
export default function CSOverview() {
  const { statistics, loading, error, lastUpdated, refresh } = useCSStatistics()
  const [agentNames, setAgentNames] = useState({})
  const [isSpamDialogOpen, setIsSpamDialogOpen] = useState(false)
  const [spamDialogRefreshKey, setSpamDialogRefreshKey] = useState(0)

  // Base URL for ticket navigation
  const ticketsBaseUrl = "/admin/customer-support/tickets"

  // Handle navigation to filtered ticket views
  const navigateToTickets = (filterParams) => {
    if (typeof window !== "undefined") {
      const queryString = new URLSearchParams(filterParams).toString()
      const url = queryString
        ? `${ticketsBaseUrl}?${queryString}`
        : ticketsBaseUrl
      navigate(url)
    }
  }

  // Handle when a ticket is selected from the spam dialog
  const handleSelectTicket = (ticketId) => {
    navigate(ticketsBaseUrl + `/${ticketId}`)
  }

  // Format response time to be human-readable using moment
  const formatResponseTime = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A"

    const duration = moment.duration(seconds, "seconds")

    if (seconds < 60) {
      return `${duration.seconds()} sec`
    } else if (seconds < 3600) {
      return `${duration.minutes()} min`
    } else {
      return `${duration.hours()}h ${duration.minutes()}m`
    }
  }

  // Define KPI cards as a data structure
  const primaryKPICards = [
    {
      id: "pendingReply",
      title: "Pending Reply",
      getValue: (stats) => stats?.pendingReply || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_error",
      isHighlighted: true,
      icon: <ExclamationTriangleIcon className="w-7 h-7 text-admin_error" />,
      onClick: () => navigateToTickets({ replyStatus: "pending" }),
      isClickable: true,
    },
    {
      id: "openTickets",
      title: "Open",
      getValue: (stats) => stats?.openTickets || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_primary",
      icon: <TicketIcon className="w-8 h-8 text-admin_primary" />,
      onClick: () => navigateToTickets({ status: "open" }),
      isClickable: true,
    },
    {
      id: "pendingTickets",
      title: "Pending",
      getValue: (stats) => stats?.pendingTickets || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_warning",
      icon: (
        <ClipboardDocumentCheckIcon className="w-7 h-7 text-admin_warning" />
      ),
      onClick: () => navigateToTickets({ status: "pending" }),
      isClickable: true,
    },
    {
      id: "resolvedTickets",
      title: "Resolved",
      getValue: (stats) => stats?.resolvedTicketsCount || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_success",
      icon: <CheckCircleIcon className="w-7 h-7 text-admin_success" />,
      onClick: () => navigateToTickets({ status: "resolved" }),
      isClickable: true,
    },
  ]

  const secondaryKPICards = [
    {
      id: "responseTime",
      title: "Response Time",
      getValue: (stats) => formatResponseTime(stats?.averageResponseTime),
      bgColor: "bg-admin_paper",
      textColor: "text-admin_progress",
      icon: <ClockIcon className="w-7 h-7 text-admin_progress" />,
      isClickable: false, // Not clickable - just shows info
    },
    {
      id: "unassigned",
      title: "Unassigned",
      getValue: (stats) => stats?.unassigned || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_warning",
      icon: <UserIcon className="w-7 h-7 text-admin_warning" />,
      onClick: () => navigateToTickets({ agent: "unassigned" }),
      isClickable: true,
    },
    {
      id: "totalTickets",
      title: "Total Tickets",
      getValue: (stats) => stats?.totalTickets || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_text",
      icon: <ArrowTrendingUpIcon className="w-8 h-8 text-admin_text" />,
      onClick: () => navigateToTickets({}), // Navigate to tickets with no filters
      isClickable: true,
    },
    {
      id: "spamTickets",
      title: "Spam",
      getValue: (stats) => stats?.spamTickets || 0,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_error",
      icon: <ShieldExclamationIcon className="w-8 h-8 text-admin_error" />,
      // Open spam dialog directly
      onClick: () => setIsSpamDialogOpen(true),
      isClickable: true,
    },
  ]

  // Table cards definition
  const tableCards = [
    {
      id: "topAgents",
      title: "Top Agents",
      getData: (stats) => stats?.topAgents || [],
      keyField: "agentId",
      valueField: "count",
      keyRenderer: (item) =>
        agentNames[item.agentId] || `Agent ${item.agentId.substring(0, 4)}`,
      bgColor: "bg-admin_paper",
      textColor: "text-admin_text",
      icon: <UserGroupIcon className="w-6 h-6 text-admin_primary" />,
      maxItems: 5,
      rowClick: (item) => {
        const agentName = agentNames[item.agentId]
        if (agentName) {
          // Replace spaces with + for URL compatibility
          navigateToTickets({ agent: agentName.replace(/\s+/g, "+") })
        }
      },
      isClickable: true,
    },
    {
      id: "tags",
      title: "Popular Tags",
      getData: (stats) => stats?.tags || [],
      keyField: "tag",
      valueField: "count",
      bgColor: "bg-admin_paper",
      textColor: "text-admin_text",
      icon: <TagIcon className="w-6 h-6 text-admin_success" />,
      maxItems: 5,
      rowClick: (item) => {
        // Navigate to tickets filtered by this tag
        navigateToTickets({ tags: item.tag.replace(/\s+/g, "+") })
      },
      isClickable: true,
    },
  ]

  // Fetch agent details to get names
  const fetchAgentDetails = async (agents) => {
    try {
      const response = await GetAgents()
      if (response.result && response.data) {
        // Create a map of agent IDs to names
        const nameMap = {}
        response.data.forEach((agent) => {
          nameMap[agent.id] = agent.name || agent.email || "Unknown"
        })
        setAgentNames(nameMap)
      }
    } catch (err) {
      console.error("Error fetching agent details:", err)
    }
  }

  // Update agent names when statistics change
  useEffect(() => {
    if (statistics?.topAgents && statistics.topAgents.length > 0) {
      fetchAgentDetails(statistics.topAgents)
    }
  }, [statistics])

  // Fallback when data is loading or not available
  if (loading && !statistics) {
    return (
      <div className="animate-pulse p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-admin_paper rounded-xl h-36 shadow-md"
            ></div>
          ))}
        </div>
      </div>
    )
  }

  // Fallback when there's an error and no previous data
  if (error && !statistics) {
    return (
      <div className="p-8">
        <div className="bg-admin_error/30 rounded-lg p-4 mb-4">
          <Typography variant="admin_txt1" className="text-admin_error mb-2">
            Error loading statistics: {error}
          </Typography>
        </div>

        <Button onClick={refresh} variant="admin_primary">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="p-8 bg-admin_canvas h-full">
      <div className="flex justify-between items-center mb-8">
        <Typography component="h1" variant="admin_h3">
          Support Overview
        </Typography>

        {lastUpdated && (
          <div className="bg-admin_paper rounded-lg px-4 py-2 shadow-sm">
            <Typography
              variant="admin_txt1"
              className="text-admin_text_secondary my-0"
            >
              Last updated: {moment(lastUpdated).fromNow()}
            </Typography>
          </div>
        )}
      </div>

      {/* Top Row - Main Statistics Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6 mb-4 xl:mb-6">
        {primaryKPICards.map((card) => (
          <KPICard
            key={card.id}
            title={card.title}
            value={card.getValue(statistics)}
            bgColor={card.bgColor}
            textColor={card.textColor}
            isHighlighted={card.isHighlighted}
            icon={card.icon}
            onClick={card.onClick}
            isClickable={card.isClickable}
          />
        ))}
      </div>

      {/* Middle Row - Secondary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6 mb-8">
        {secondaryKPICards.map((card) => (
          <KPICard
            key={card.id}
            title={card.title}
            value={card.getValue(statistics)}
            bgColor={card.bgColor}
            textColor={card.textColor}
            isHighlighted={card.isHighlighted}
            icon={card.icon}
            onClick={card.onClick}
            isClickable={card.isClickable}
          />
        ))}
      </div>

      {/* Bottom Row - Table Cards for Top Agents and Tags */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tableCards.map((card) => (
          <TableKPICard
            key={card.id}
            title={card.title}
            data={card.getData(statistics)}
            keyField={card.keyField}
            valueField={card.valueField}
            keyRenderer={card.keyRenderer}
            bgColor={card.bgColor}
            textColor={card.textColor}
            icon={card.icon}
            maxItems={card.maxItems}
            rowClick={card.rowClick}
            isClickable={card.isClickable}
          />
        ))}
      </div>

      {/* Show error as a banner if refreshing fails but we have old data */}
      {error && (
        <div className="mt-8 bg-admin_error/20 text-admin_error p-4 rounded-lg flex justify-between items-center shadow-md">
          <span>Error refreshing: {error}</span>

          <Button onClick={refresh} variant="admin_primary_red">
            Retry
          </Button>
        </div>
      )}

      {/* Spam Tickets Dialog - directly embedded in this component */}
      <SpamTicketsDialog
        isOpen={isSpamDialogOpen}
        onClose={() => setIsSpamDialogOpen(false)}
        parentRefreshKey={spamDialogRefreshKey}
        onTicketsUpdated={() => {
          setSpamDialogRefreshKey((prev) => prev + 1)
          // Also refresh the statistics to reflect any changes
          refresh()
        }}
        onSelectTicket={handleSelectTicket}
      />
    </div>
  )
}
